import React, { useState } from 'react';
import { Plus, Edit2, Trash2, FileText, Download, Search } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import { generateId, formatDate } from '../../utils/helpers';
import { getCurrentAcademicYear, getAcademicYearOptions } from '../../utils/academicYear';
import DateInput from '../../components/common/DateInput';

const EXAM_TYPES = ['Formative Assessment', 'Summative Assessment', 'Slip Test', 'Revision Test', 'Unit Test', 'Mid Term', 'Final Exam'];

const typeColors = {
  'Formative Assessment': 'bg-blue-100 text-blue-700',
  'Summative Assessment': 'bg-purple-100 text-purple-700',
  'Slip Test': 'bg-orange-100 text-orange-700',
  'Revision Test': 'bg-green-100 text-green-700',
  'Unit Test': 'bg-teal-100 text-teal-700',
  'Mid Term': 'bg-yellow-100 text-yellow-700',
  'Final Exam': 'bg-red-100 text-red-700',
};

const blank = (academicYear) => ({
  title: '', subjectId: '', classIds: [], examType: 'Unit Test',
  academicYear: academicYear || getCurrentAcademicYear(), date: '', description: '', fileUrl: '',
});

function QuestionPapers() {
  const { questionPapers, setQuestionPapers, classes, subjects, staff, students, academicYear } = useApp();
  const { currentUser } = useAuth();

  const isAdmin = currentUser?.role === 'admin';
  const isTeacher = currentUser?.role === 'teacher';
  const canManage = isAdmin || isTeacher;

  // For teacher: only show papers linked to their classes/subjects
  const teacherProfile = isTeacher ? staff.find(s => s.id === currentUser?.profileId) : null;
  const teacherClassIds = teacherProfile?.classIds || [];
  const teacherSubjectIds = teacherProfile?.subjectIds || [];

  // For student: get their classId
  const studentProfile = currentUser?.role === 'student'
    ? students.find(s => s.id === currentUser?.profileId)
    : null;
  const studentClassId = studentProfile?.classId;

  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(() => blank(academicYear));
  const [editing, setEditing] = useState(null);

  const visiblePapers = questionPapers.filter(p => {
    if (currentUser?.role === 'student') {
      if (studentClassId && !(p.classIds || []).includes(studentClassId)) return false;
    }
    if (isTeacher) {
      const classMatch = !teacherClassIds.length || (p.classIds || []).some(cid => teacherClassIds.includes(cid));
      const subjectMatch = !teacherSubjectIds.length || teacherSubjectIds.includes(p.subjectId);
      if (!classMatch && !subjectMatch) return false;
    }
    if (filterClass && !(p.classIds || []).includes(filterClass)) return false;
    if (filterSubject && p.subjectId !== filterSubject) return false;
    if (filterType && p.examType !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      const sub = subjects.find(s => s.id === p.subjectId);
      if (!p.title.toLowerCase().includes(q) && !(sub?.name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }).slice().reverse();

  const openAdd = () => { setForm(blank(academicYear)); setEditing(null); setModal(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p); setModal(true); };
  const remove = (id) => { if (window.confirm('Delete this question paper?')) setQuestionPapers(prev => prev.filter(p => p.id !== id)); };

  const save = () => {
    if (!form.title.trim()) return;
    if (editing) {
      setQuestionPapers(prev => prev.map(p => p.id === editing.id ? { ...form, id: editing.id } : p));
    } else {
      setQuestionPapers(prev => [...prev, { ...form, id: generateId('QP'), uploadedBy: currentUser?.name, uploadedAt: new Date().toISOString().split('T')[0] }]);
    }
    setModal(false);
  };

  const toggleClass = (classId) => {
    setForm(f => ({
      ...f,
      classIds: f.classIds.includes(classId) ? f.classIds.filter(id => id !== classId) : [...f.classIds, classId],
    }));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Question Papers</h1>
          <p className="text-gray-500 text-sm">{visiblePapers.length} paper{visiblePapers.length !== 1 ? 's' : ''} available</p>
        </div>
        {canManage && (
          <button onClick={openAdd} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
            <Plus size={16} /> Add Question Paper
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by title or subject…"
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
          />
        </div>
        <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All Classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
        </select>
        <select value={filterSubject} onChange={e => setFilterSubject(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All Types</option>
          {EXAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Papers list */}
      {visiblePapers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center py-16 text-gray-400">
          <FileText size={40} className="mb-3 opacity-30" />
          <p className="text-sm">No question papers found</p>
          {canManage && <p className="text-xs mt-1">Click "Add Question Paper" to get started</p>}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visiblePapers.map(paper => {
            const subject = subjects.find(s => s.id === paper.subjectId);
            const paperClasses = (paper.classIds || []).map(cid => classes.find(c => c.id === cid)).filter(Boolean);
            return (
              <div key={paper.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="bg-purple-50 text-purple-600 rounded-lg p-2 flex-shrink-0">
                      <FileText size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 leading-tight">{paper.title}</p>
                      {subject && <p className="text-xs text-gray-500 mt-0.5">{subject.name} {subject.code ? `(${subject.code})` : ''}</p>}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => openEdit(paper)} className="p-1.5 hover:bg-purple-50 rounded-lg text-purple-400"><Edit2 size={13} /></button>
                      <button onClick={() => remove(paper.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400"><Trash2 size={13} /></button>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[paper.examType] || 'bg-gray-100 text-gray-700'}`}>
                    {paper.examType}
                  </span>
                  {paperClasses.map(c => (
                    <span key={c.id} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {c.name}-{c.section}
                    </span>
                  ))}
                </div>

                {paper.description && (
                  <p className="text-xs text-gray-500 line-clamp-2">{paper.description}</p>
                )}

                <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-50">
                  <div className="text-xs text-gray-400">
                    {paper.academicYear}
                    {paper.date && <span className="ml-2">· {formatDate(paper.date)}</span>}
                    {paper.uploadedBy && <span className="ml-2">· {paper.uploadedBy}</span>}
                  </div>
                  {paper.fileUrl && (
                    <a href={paper.fileUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                      <Download size={12} /> Download
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Edit Question Paper' : 'Add Question Paper'} size="lg">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Title <span className="text-red-500">*</span></label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Mathematics Unit Test – Chapter 3"
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Subject</label>
              <select value={form.subjectId} onChange={e => setForm(f => ({ ...f, subjectId: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                <option value="">Select subject</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Exam Type</label>
              <select value={form.examType} onChange={e => setForm(f => ({ ...f, examType: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                {EXAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Academic Year</label>
              <select value={form.academicYear} onChange={e => setForm(f => ({ ...f, academicYear: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                {getAcademicYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Date</label>
              <DateInput value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Classes</label>
            <div className="flex flex-wrap gap-1.5">
              {classes.map(c => (
                <button key={c.id} type="button" onClick={() => toggleClass(c.id)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                    form.classIds.includes(c.id)
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}>
                  {c.name}-{c.section}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">File URL (optional)</label>
            <input value={form.fileUrl} onChange={e => setForm(f => ({ ...f, fileUrl: e.target.value }))}
              placeholder="https://drive.google.com/…"
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description / Notes</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} placeholder="Optional notes about this paper…"
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setModal(false)} className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} disabled={!form.title.trim()}
            className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
            {editing ? 'Save Changes' : 'Add Paper'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default QuestionPapers;
